import './styles.scss';

import { ComboBox } from 'src/components/credit/ui/comboBox/ComboBox';
import { FundingReservesService } from 'src/services/reserves.service';
import { IReservesObject } from 'src/services/models/reserves.service';
import { isFirstRender } from 'src/utils/isFirstMount';
import { matchVIN } from 'src/utils/validators';
import { useLocalization } from 'src/utils/useLocalization';
//import { useTranslation } from 'react-i18next';
import {
    compareDates,
    compareNumbers,
    compareStrings,
} from 'src/utils/helpers';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
interface ISearchProps {
    content: IReservesObject[];
    updateTableContent: Dispatch<SetStateAction<IReservesObject[]>>;
    updateListContent: Dispatch<SetStateAction<any>>;
    setCurrentMonth: Dispatch<SetStateAction<any>>;
    dealerID: string;
    setLoading: Dispatch<SetStateAction<boolean>>;
    sortType: string;
    sortOrder: boolean;
    isActivity: boolean;
    brandCode: string;
}

const ONE = 1;
const CERO = 0;
const TEN = 10;

// Date options hold the month's last day (YYYY-MM-DD); the range must start on day 01.
const toFirstDayOfMonth = (date: string) => date.replace(/\d{2}$/, '01');

export const Search: React.FC<ISearchProps> = ({
    content,
    updateTableContent,
    updateListContent,
    setCurrentMonth,
    dealerID,
    setLoading,
    sortType,
    sortOrder,
    isActivity,
    brandCode,
}) => {
    //const { t, i18n } = useTranslation();
    const { reserves } = useLocalization();
    const [searchTerm, setSearchTerm] = useState('');
    const [monthDate, setMonthDate] = useState<string>('');
    const [endMonthDate, setEndMonthDate] = useState<string>('');
    const [rawData, setRawData] = useState<IReservesObject[]>([]);

    const firstRender = isFirstRender();

    const montharray = [
        reserves.FILTER_JANUARY,
        reserves.FILTER_FEBRUARY,
        reserves.FILTER_MARCH,
        reserves.FILTER_APRIL,
        reserves.FILTER_MAY,
        reserves.FILTER_JUNE,
        reserves.FILTER_JULY,
        reserves.FILTER_AUGUST,
        reserves.FILTER_SEPTEMBER,
        reserves.FILTER_OCTOBER,
        reserves.FILTER_NOVEMBER,
        reserves.FILTER_DECEMBER,
    ];

    const getDateOptions = () => {
        const currentDate = new Date();
        const startDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + ONE,
            CERO,
        );
        const limitDate = new Date(currentDate.getFullYear() - ONE, ONE, CERO);

        const dateArray = [];

        while (startDate >= limitDate) {
            dateArray.push({
                value: new Date(startDate).toISOString().slice(CERO, TEN),
                label: `${
                    montharray[startDate.getMonth()]
                } ${startDate.getFullYear()}`,
            });
            startDate.setFullYear(
                startDate.getFullYear(),
                startDate.getMonth(),
                CERO,
            );
        }

        return dateArray;
    };

    const dateOptions = getDateOptions();

    const service = new FundingReservesService();

    enum FilterNames {
        Search = 'Search',
        Date = 'Date',
    }

    const resetFilters = async (filtersToReset: FilterNames[]) => {
        const all = !filtersToReset.length;
        //setResetState(true);
        if (!!all || !!filtersToReset.includes(FilterNames.Search)) {
            setSearchTerm('');
        }

        if (!!all || !!filtersToReset.includes(FilterNames.Date)) {
            if (isActivity) {
                setMonthDate(dateOptions[ONE].value);
                setEndMonthDate(dateOptions[CERO].value);
            } else {
                setMonthDate(dateOptions[CERO].value);
            }
        }
    };

    const handleReset = async () => {
        await resetFilters([]);
    };

    const applySort = (data: any[]) => {
        let sorted = [...data];
        switch (sortType) {
            case 'customerName':
                sorted = compareStrings(sorted, 'customerName', sortOrder);
                break;
            case 'transactionType':
                sorted = compareStrings(sorted, 'transactionType', sortOrder);
                break;
            case 'amount':
                sorted = compareNumbers(sorted, 'amountFinanced', sortOrder);
                break;
            case 'payment':
                sorted = compareNumbers(sorted, 'paymentAmount', sortOrder);
                break;
            case 'endOfMonth':
                sorted = compareDates(sorted, 'endOfMonth', sortOrder);
                break;
            default:
                break;
        }
        return sorted;
    };

    const searchDefaultData = async () => {
        setLoading(true);
        setMonthDate(
            isActivity ? dateOptions[ONE].value : dateOptions[CERO].value,
        );

        if (isActivity) {
            setMonthDate(dateOptions[ONE].value);
            setEndMonthDate(dateOptions[CERO].value);
        } else {
            setMonthDate(dateOptions[CERO].value);
        }
        try {
            const response = await service.fundingReserves(
                dealerID,
                brandCode,
                toFirstDayOfMonth(monthDate),
                isActivity ? endMonthDate : monthDate,
            );
            if (!!response && !!response.data) {
                let tableData = response.data[0];
                const listData = response.data[1];

                if (sortType) {
                    tableData = applySort(tableData);
                }

                if (!isActivity) {
                    const currentMonth = new Date(monthDate);
                    setCurrentMonth({
                        month: currentMonth.getMonth(),
                        year: currentMonth.getFullYear(),
                    });
                } else {
                    setCurrentMonth(null);
                }

                setRawData(tableData);
                updateTableContent(tableData);
                updateListContent(listData[listData.length - ONE]);
                setLoading(false);
            } else {
                setRawData([]);
                updateTableContent([]);
                updateListContent({});
                setLoading(false);
            }
        } catch {
            setRawData([]);
            updateTableContent([]);
            updateListContent({});
            setLoading(false);
        }
    };

    const handleDateReceivedChange = async () => {
        setLoading(true);
        try {
            const response = await service.fundingReserves(
                dealerID,
                brandCode,
                toFirstDayOfMonth(monthDate),
                isActivity ? endMonthDate : monthDate,
            );
            if (!!response && !!response.data) {
                let tableData = response.data[0];
                const listData = response.data[1];

                if (sortType) {
                    tableData = applySort(tableData);
                }

                if (!isActivity) {
                    const currentMonth = new Date(monthDate);
                    setCurrentMonth({
                        month: currentMonth.getMonth(),
                        year: currentMonth.getFullYear(),
                    });
                } else {
                    setCurrentMonth(null);
                }

                setRawData(tableData);
                updateTableContent(tableData);
                updateListContent(listData[listData.length - ONE]);
                setLoading(false);
            } else {
                setRawData([]);
                updateTableContent([]);
                updateListContent({});
                setLoading(false);
            }
        } catch {
            setRawData([]);
            updateTableContent([]);
            updateListContent({});
            setLoading(false);
        }
    };

    useEffect(() => {
        searchDefaultData();
    }, []);

    useEffect(() => {
        if (content) {
            updateTableContent(applySort(content));
        }
    }, [sortOrder, sortType]);

    const shouldMakeRequest = !firstRender;

    useEffect(() => {
        if (shouldMakeRequest) {
            if (isActivity && !endMonthDate) {
                setMonthDate(dateOptions[ONE].value);
                setEndMonthDate(dateOptions[CERO].value);
            }

            if (monthDate && (!isActivity || endMonthDate)) {
                handleDateReceivedChange();
            }
        }
    }, [monthDate, endMonthDate, isActivity]);

    const resetFilteredData = () => {
        setSearchTerm('');
        updateTableContent(rawData);
    };

    const handleEnter = (evt: any) => {
        if (evt.key === 'Enter') {
            if (searchTerm == '') {
                updateTableContent(rawData);
            } else if (matchVIN(searchTerm)) {
                updateTableContent(
                    rawData.filter((item) =>
                        item.vin.includes(searchTerm.toUpperCase()),
                    ),
                );
            } else {
                updateTableContent(
                    rawData.filter((item) =>
                        item.customerName.includes(searchTerm.toUpperCase()),
                    ),
                );
            }
        }
    };

    return (
        <div className='reserves-search-bar search-row c-input'>
            <div className='search-div'>
                <div className='c-input search-text'>
                    <span id='searchIcon'>
                        <i
                            className='c-icon c-icon--[semantic-search]'
                            aria-hidden='true'
                            role={'img'}
                        ></i>
                    </span>
                    <input
                        id='searchInput'
                        className='c-input__input'
                        type={'text'}
                        value={searchTerm}
                        placeholder={reserves.SEARCH_BAR}
                        onChange={(evt: any) =>
                            setSearchTerm(evt.target.value.trim())
                        }
                        onKeyDown={handleEnter}
                        role='searchQuery'
                    />
                    <span
                        id='clearSearch'
                        className={`c-input__addon ${
                            !searchTerm ? 'invisible' : ''
                        }`}
                        onClick={() => resetFilteredData()}
                        style={{ borderColor: '#d1dcde' }}
                        data-testid='clearSearch'
                        role='clearSearch'
                    >
                        <i
                            className='c-icon  c-icon--[semantic-close]'
                            aria-hidden='true'
                            role={'img'}
                        ></i>
                    </span>
                </div>
            </div>
            <div
                className={isActivity ? 'select-ph-date' : 'select-ph-enddate'}
            >
                <ComboBox
                    name={'monthDate'}
                    options={dateOptions}
                    defaultValue={
                        isActivity ? dateOptions[1].label : dateOptions[0].label
                    }
                    placeholder={''}
                    onSelect={setMonthDate}
                    selectedOption={monthDate}
                />
            </div>
            {isActivity && (
                <div className='select-ph-enddate'>
                    <ComboBox
                        name={'endMonthDate'}
                        options={dateOptions}
                        defaultValue={dateOptions[0].label}
                        placeholder={''}
                        onSelect={setEndMonthDate}
                        selectedOption={endMonthDate}
                    />
                </div>
            )}

            <div className='reset-re-search'>
                <button
                    className={'c-btn--secondary c-btn c-btn--small'}
                    type={'button'}
                    onClick={handleReset}
                >
                    <span className='c-btn__text'>{reserves.FILTER_RESET}</span>
                </button>
            </div>
        </div>
    );
};

export default Search;
